import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../config';

// Generic interface for accessory attributes
export interface AccessoryAttribute {
  id: string;
  value: string;
  category?: string; // Optional category filter for some attributes
  isActive: boolean;
  usageCount?: number; // Track how often this attribute is used
  createdAt: Date;
  updatedAt: Date;
}

// Collection names for different attributes
export const COLLECTIONS = {
  BRANDS: 'accessoryBrands',
  MODELS: 'accessoryModels',
  SIZES: 'accessorySizes',
  COLORS: 'accessoryColors',
  LOCATIONS: 'accessoryLocations',
  MAINTENANCE_INTERVALS: 'accessoryMaintenanceIntervals'
} as const;

// Generic CRUD operations for any attribute collection
export const createAttribute = async (
  collectionName: string, 
  attribute: Omit<AccessoryAttribute, 'id'>
): Promise<void> => {
  const attributeCollection = collection(db, collectionName);
  const docRef = doc(attributeCollection);
  await setDoc(docRef, {
    ...attribute,
    createdAt: new Date(),
    updatedAt: new Date(),
  });
};

export const getAttributes = async (collectionName: string): Promise<AccessoryAttribute[]> => {
  const q = query(
    collection(db, collectionName),
    where('isActive', '==', true),
    orderBy('usageCount', 'desc'),
    orderBy('value', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => {
    const data = doc.data() as any;
    return {
      id: doc.id,
      value: data.value || '',
      category: data.category,
      isActive: data.isActive ?? true,
      usageCount: data.usageCount || 0,
      createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
        ? data.createdAt.toDate() 
        : data.createdAt ? new Date(data.createdAt) : new Date(),
      updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
        ? data.updatedAt.toDate()
        : data.updatedAt ? new Date(data.updatedAt) : new Date(),
    } as AccessoryAttribute;
  });
};

export const getAttributesByCategory = async (
  collectionName: string, 
  category?: string
): Promise<AccessoryAttribute[]> => {
  let q;
  if (category) {
    q = query(
      collection(db, collectionName),
      where('isActive', '==', true),
      where('category', '==', category),
      orderBy('usageCount', 'desc'),
      orderBy('value', 'asc')
    );
  } else {
    q = query(
      collection(db, collectionName),
      where('isActive', '==', true),
      orderBy('usageCount', 'desc'),
      orderBy('value', 'asc')
    );
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => {
    const data = doc.data() as any;
    return {
      id: doc.id,
      value: data.value || '',
      category: data.category,
      isActive: data.isActive ?? true,
      usageCount: data.usageCount || 0,
      createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
        ? data.createdAt.toDate() 
        : data.createdAt ? new Date(data.createdAt) : new Date(),
      updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
        ? data.updatedAt.toDate()
        : data.updatedAt ? new Date(data.updatedAt) : new Date(),
    } as AccessoryAttribute;
  });
};

export const addAttributeIfNotExists = async (
  collectionName: string,
  value: string,
  category?: string
): Promise<void> => {
  const existingAttributes = await getAttributesByCategory(collectionName, category);
  const attributeExists = existingAttributes.some(attr => 
    attr.value.toLowerCase() === value.toLowerCase()
  );
  
  if (!attributeExists && value.trim()) {
    await createAttribute(collectionName, {
      value: value.trim(),
      category,
      isActive: true,
      usageCount: 1,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  } else if (attributeExists) {
    // Increment usage count for existing attribute
    const existingAttr = existingAttributes.find(attr => 
      attr.value.toLowerCase() === value.toLowerCase()
    );
    if (existingAttr) {
      await updateAttribute(collectionName, existingAttr.id, {
        usageCount: (existingAttr.usageCount || 0) + 1,
        updatedAt: new Date()
      });
    }
  }
};

export const updateAttribute = async (
  collectionName: string,
  id: string, 
  updates: Partial<AccessoryAttribute>
): Promise<void> => {
  const docRef = doc(db, collectionName, id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: new Date(),
  });
};

// Specific functions for each attribute type
export const getBrands = () => getAttributes(COLLECTIONS.BRANDS);
export const getModels = () => getAttributes(COLLECTIONS.MODELS);
export const getSizes = () => getAttributes(COLLECTIONS.SIZES);
export const getColors = () => getAttributes(COLLECTIONS.COLORS);
export const getLocations = () => getAttributes(COLLECTIONS.LOCATIONS);
export const getMaintenanceIntervals = () => getAttributes(COLLECTIONS.MAINTENANCE_INTERVALS);

export const addBrandIfNotExists = (value: string) => 
  addAttributeIfNotExists(COLLECTIONS.BRANDS, value);
export const addModelIfNotExists = (value: string) => 
  addAttributeIfNotExists(COLLECTIONS.MODELS, value);
export const addSizeIfNotExists = (value: string) => 
  addAttributeIfNotExists(COLLECTIONS.SIZES, value);
export const addColorIfNotExists = (value: string) => 
  addAttributeIfNotExists(COLLECTIONS.COLORS, value);
export const addLocationIfNotExists = (value: string) => 
  addAttributeIfNotExists(COLLECTIONS.LOCATIONS, value);
export const addMaintenanceIntervalIfNotExists = (value: string) => 
  addAttributeIfNotExists(COLLECTIONS.MAINTENANCE_INTERVALS, value);

// Initialize default values for each collection
export const initializeDefaultAttributes = async (): Promise<void> => {
  const defaultBrands = [
    'Coleman', 'Intex', 'Bestway', 'Sea Eagle', 'BOTE', 'YOLO', 'Red Paddle Co',
    'Pelican', 'Lifetime', 'Sun Dolphin', 'Future Beach', 'Perception'
  ];

  const defaultModels = [
    'Explorer', 'Challenger', 'Seahawk', 'Mariner', 'Adventure', 'Sport',
    'Classic', 'Pro', 'Elite', 'Standard', 'Deluxe', 'Basic'
  ];

  const defaultSizes = [
    'XS', 'S', 'M', 'L', 'XL', 'XXL', 'One Size', '8ft', '10ft', '12ft', '14ft', '16ft',
    'Adult', 'Child', 'Infant', 'Universal', 'Adjustable'
  ];

  const defaultColors = [
    'Red', 'Blue', 'Green', 'Yellow', 'Orange', 'Black', 'White', 'Gray', 'Navy',
    'Teal', 'Purple', 'Pink', 'Lime', 'Aqua', 'Multi-Color', 'Clear'
  ];

  const defaultLocations = [
    'Storage Room A', 'Storage Room B', 'Dock Box 1', 'Dock Box 2', 'Dock Box 3',
    'Equipment Shed', 'Marina Office', 'Boat House', 'Rental Counter', 'Beach Storage',
    'Life Jacket Station', 'Equipment Rack', 'Safety Station'
  ];

  const defaultMaintenanceIntervals = [
    '30', '60', '90', '180', '365', '730' // days
  ];

  // Check if collections are empty before initializing
  const [brands, models, sizes, colors, locations, intervals] = await Promise.all([
    getBrands(),
    getModels(),
    getSizes(),
    getColors(),
    getLocations(),
    getMaintenanceIntervals()
  ]);

  // Initialize only if collections are empty
  if (brands.length === 0) {
    for (const brand of defaultBrands) {
      await addBrandIfNotExists(brand);
    }
  }

  if (models.length === 0) {
    for (const model of defaultModels) {
      await addModelIfNotExists(model);
    }
  }

  if (sizes.length === 0) {
    for (const size of defaultSizes) {
      await addSizeIfNotExists(size);
    }
  }

  if (colors.length === 0) {
    for (const color of defaultColors) {
      await addColorIfNotExists(color);
    }
  }

  if (locations.length === 0) {
    for (const location of defaultLocations) {
      await addLocationIfNotExists(location);
    }
  }

  if (intervals.length === 0) {
    for (const interval of defaultMaintenanceIntervals) {
      await addMaintenanceIntervalIfNotExists(interval);
    }
  }
}; 